/**
 * micro:bit 놀이터 차시 장면 — 1단계 묶음 둘째(화면 읽기 ① · 입력 따라하기 ② · PICNIC-MB-FIELD-001 v0.2 §4.2 · §4.3).
 *
 * 순수 상태(DOM · 시계 없음 — 호출하는 쪽이 지난 시간 ms · 지금 시각 ms 와 그때의 보드 모습을 넘긴다). 같은 입력이면 같은 결과다.
 * 놀이터는 **보드가 보여 준 것** 으로만 진행한다 — 아이의 프로그램이 숫자 · 아이콘을 맞게 그려야 놀이가 맞게 흘러간다.
 *  - 슛 골 세기판(L07): 놀이터가 3초마다 슛 — 들어가면 A, 빗나가면 B. 보드가 그린 숫자가 실제 골 수와 같은가.
 *  - 가위바위보(L08): 보드의 손(가위 · 작은 네모 = 바위 · 큰 네모 = 보)을 읽어 놀이터 친구와 한 판.
 *  - 단거리 출발대(L09): 화면이 켜진 순간부터 A 를 누를 때까지(메시지 시각 · 0.1초 단위로 읽힌다).
 *  - 골든벨(L10): 놀이터가 O/X 문제 — 보드의 체크(O) · 엑스(X) 아이콘이 답.
 *  - 주사위(L11 · LE05): 숫자 한 자리(1~6) 또는 점 그림(서로 붙지 않은 점 1~6개)을 눈으로 읽는다.
 *  - 볼 미로(L12): 기울기로 공을 굴려 구멍에.
 *  - 수평계(L14): 수평일 때 체크가 3초 서 있으면 벽돌 하나.
 */
import type { MbFrame } from "./live";
import { iconOf, isBlank } from "./live";
import { bitsOfLeds, digitOf } from "./font";

const rise = (now: boolean, was: boolean) => now && !was;

// ── 슛 골 세기판(L07) ────────────────────────────────────────────────

export const SHOT_MS = 3000;
export const GOALS_GOAL = 5;
/** 별 셋까지 슛을 이어 간다(3D 무대 별 둘 · 셋) */
export const GOALS_MAX = 50;
/** 슛 결과 차례(난수 없음 — 시험 · 리플레이가 같다) */
const SHOTS: readonly boolean[] = [true, false, true, true, false, true, false, true];

export interface Goals {
  /** 놀이 시계(보드가 연결돼 있는 동안만 간다) */
  readonly clock: number;
  readonly shots: readonly boolean[];
  /** 보드가 마지막으로 그린 숫자 */
  readonly board: number | null;
  /** 보드 숫자가 실제 골 수와 맞았던 가장 큰 골 수(별 둘 · 셋) */
  readonly matched?: number;
}

export const initialGoals = (): Goals => ({ clock: 0, shots: [], board: null });
export const goalsIn = (s: Goals): number => s.shots.filter(Boolean).length;

export function stepGoals(s: Goals, dtMs: number, frame: MbFrame | null): Goals {
  if (!frame) return s;
  const d = digitOf(frame.leds);
  const board = d ?? s.board;
  // 실제 골이 5개가 되면 슛을 멈춘다 — 보드가 5 를 그리면 끝
  const matched = board !== null && board === goalsIn(s) ? Math.max(s.matched ?? 0, board) : s.matched;
  // 목표(5골)를 맞힌 뒤에도 별 셋(15골)까지 슛을 이어 간다 — 목표 전에는 처음처럼 5골에서 멈춘다
  const cap = (matched ?? 0) >= GOALS_GOAL ? GOALS_MAX : GOALS_GOAL;
  if (goalsIn(s) >= cap) return board === s.board && matched === s.matched ? s : { ...s, board, matched };
  const clock = s.clock + dtMs;
  const due = Math.floor(clock / SHOT_MS);
  const shots = due > s.shots.length ? [...s.shots, SHOTS[s.shots.length % SHOTS.length]!] : s.shots;
  return { clock, shots, board, ...(matched !== undefined ? { matched } : {}) };
}
/** 보드 숫자 = 실제 골 수 */
export const goalsMatch = (s: Goals): boolean => s.board !== null && s.board === goalsIn(s);
export const goalsDone = (s: Goals): boolean => (goalsIn(s) >= GOALS_GOAL && goalsMatch(s)) || (s.matched ?? 0) >= GOALS_GOAL;

// ── 가위바위보(L08) ──────────────────────────────────────────────────

export type Hand = "rock" | "paper" | "scissors";
/** MakeCode 가위바위보 따라하기의 손 — 바위 = 작은 네모 · 보 = 큰 네모 · 가위 = 가위 */
export const HAND_OF_ICON: Readonly<Record<string, Hand>> = { SmallSquare: "rock", Square: "paper", Scissors: "scissors" };
const FRIEND: readonly Hand[] = ["paper", "rock", "scissors", "scissors", "paper", "rock"];
export const RPS_ROUNDS = 3;
/** 같은 손을 두 번 세지 않는 사이 */
const ROUND_GAP_MS = 600;
/**
 * 흔든 뒤 프로그램이 새 손을 그릴 때까지 기다리는 시간 — 흔들기와 화면은 같은 0.1초 소식에 함께 오지만 화면은 한 박자 늦다.
 * 바로 세면 **흔들기 전의 손** 을 세고 정작 새 손은 '너무 빨리 또'로 버린다. 그 사이 화면이 바뀌면 바뀐 손을 센다.
 */
const ARM_MS = 300;
const tickArm = (armed: number | null, shakeRise: boolean, dtMs: number) => (shakeRise ? 0 : armed === null ? null : armed + dtMs);

export interface Round { readonly me: Hand; readonly friend: Hand }
export interface Rps {
  readonly rounds: readonly Round[];
  readonly prev: string | null;
  readonly wasShake: boolean;
  /** 흔든 뒤 지난 시간(ms · 아직 판을 세지 않았으면) — 같은 손이 다시 나와 화면이 그대로여도 ARM_MS 뒤에 한 판 */
  readonly armed: number | null;
  readonly since: number;
}

export const initialRps = (): Rps => ({ rounds: [], prev: null, wasShake: false, armed: null, since: ROUND_GAP_MS });
export const handOf = (leds: readonly number[]): Hand | null => HAND_OF_ICON[iconOf(leds) ?? ""] ?? null;

export function stepRps(s: Rps, dtMs: number, frame: MbFrame | null): Rps {
  const since = s.since + dtMs;
  if (!frame) return { ...s, since };
  const bits = bitsOfLeds(frame.leds);
  const armed = tickArm(s.armed, rise(frame.shake, s.wasShake), dtMs);
  const hand = handOf(frame.leds);
  const fresh = hand !== null && (bits !== s.prev || (armed !== null && armed >= ARM_MS)) && since >= ROUND_GAP_MS;
  const rounds = fresh ? [...s.rounds, { me: hand, friend: FRIEND[s.rounds.length % FRIEND.length]! }] : s.rounds;
  return { rounds, prev: bits, wasShake: frame.shake, armed: fresh ? null : armed, since: fresh ? 0 : since };
}
/** 두 손이 만나면 — 같은 손 · 내 손이 이기는 손을 만남(me) · 친구 손이 그런 손(friend). 화면 문구는 승패 낱말 없이 무엇이 무엇을 했는지만 */
export function meet(r: Round): "same" | "me" | "friend" {
  if (r.me === r.friend) return "same";
  const over: Record<Hand, Hand> = { rock: "scissors", scissors: "paper", paper: "rock" };
  return over[r.me] === r.friend ? "me" : "friend";
}

// ── 단거리 출발대(L09) ───────────────────────────────────────────────

export const REACTION_MS = 400;
export const REACTION_GOAL = 3;

export interface Reaction {
  /** 화면이 켜진 시각(ms · 기다리는 중이면) */
  readonly litAt: number | null;
  readonly wasLit: boolean;
  readonly wasA: boolean;
  /** 잰 기록(ms) */
  readonly times: readonly number[];
  /** 불이 켜지기 전에 누름 */
  readonly early: number;
  /** 빠른 출발(REACTION_MS 안) 모두 센 수(별 둘 · 셋 — times 는 최근 10개만) */
  readonly fastN?: number;
}

export const initialReaction = (): Reaction => ({ litAt: null, wasLit: false, wasA: false, times: [], early: 0 });

/** 메시지마다 — 지금 시각(ms)과 그때의 보드. 빈 화면 → 켜짐이 출발 신호, 그 뒤 첫 A 가 출발 */
export function stepReaction(s: Reaction, nowMs: number, frame: MbFrame | null): Reaction {
  if (!frame) return s;
  const lit = !isBlank(frame.leds);
  let { litAt, times, early } = s;
  let fastN = s.fastN ?? 0;
  if (lit && !s.wasLit) litAt = nowMs;
  if (!lit) litAt = null;
  if (rise(frame.a, s.wasA)) {
    if (litAt !== null) {
      const ms = Math.max(0, nowMs - litAt);
      times = [...times, ms].slice(-10);
      if (ms <= REACTION_MS) fastN += 1;
      litAt = null;
    }
    else if (!lit) early += 1;
  }
  return { litAt, wasLit: lit, wasA: frame.a, times, early, fastN };
}
export const fastStarts = (s: Reaction): number => s.times.filter((t) => t <= REACTION_MS).length;

// ── 골든벨(L10) ──────────────────────────────────────────────────────

/** 문제 정답(문제 글은 i18n `scene.quiz.q.<n>`) */
export const QUIZ_KEY: readonly boolean[] = [true, false, true, false, true];
export const QUIZ_PASS = 4;

export interface Quiz {
  readonly q: number;
  readonly right: number;
  /** 마지막 답(문제 번호 · 답 · 맞았나) */
  readonly last: { readonly q: number; readonly answer: boolean; readonly ok: boolean } | null;
  readonly passed: boolean;
  /** 한 바퀴를 다 돌았는데 모자라 처음부터 다시 */
  readonly again: number;
  readonly prev: string | null;
  readonly wasA: boolean;
  readonly wasB: boolean;
}

export const initialQuiz = (): Quiz => ({ q: 0, right: 0, last: null, passed: false, again: 0, prev: null, wasA: false, wasB: false });

export function stepQuiz(s: Quiz, frame: MbFrame | null): Quiz {
  if (!frame || s.passed) return s;
  const bits = bitsOfLeds(frame.leds);
  const icon = iconOf(frame.leds);
  const answer = icon === "Yes" ? true : icon === "No" ? false : null;
  // 화면이 체크 · 엑스로 바뀌었거나, 같은 답을 또 낼 때(A = O 를 다시 · B = X 를 다시). 버튼이 화면보다 한 박자 먼저 오므로
  // '버튼 + 지난 답 화면' 은 세지 않는다(B 를 눌렀는데 아직 체크가 떠 있으면 O 로 세고, 곧 엑스로 바뀌면 또 센다)
  const fresh = answer !== null && (bits !== s.prev || (answer && rise(frame.a, s.wasA)) || (!answer && rise(frame.b, s.wasB)));
  const base = { ...s, prev: bits, wasA: frame.a, wasB: frame.b };
  if (!fresh) return base;
  const ok = answer === QUIZ_KEY[s.q];
  const right = s.right + (ok ? 1 : 0);
  const q = s.q + 1;
  const last = { q: s.q, answer: answer!, ok };
  if (q < QUIZ_KEY.length) return { ...base, q, right, last };
  return right >= QUIZ_PASS ? { ...base, q, right, last, passed: true } : { ...base, q: 0, right: 0, last, again: s.again + 1 };
}

// ── 주사위(L11 주사위 만들기 · LE05 흔들면 주사위) ───────────────────

export const BOARD_LEN = 15;

/** 주사위 눈 — 숫자 한 자리 1~6, 또는 서로 붙지 않은(가로 · 세로) 점 1~6개 */
export function faceOf(leds: readonly number[]): number | null {
  const d = digitOf(leds);
  if (d !== null) return d >= 1 && d <= 6 ? d : null;
  const on = leds.map((v) => v >= 5);
  let n = 0;
  for (let i = 0; i < 25; i++) {
    if (!on[i]) continue;
    n++;
    if ((i % 5 < 4 && on[i + 1]) || (i < 20 && on[i + 5])) return null;
  }
  return n >= 1 && n <= 6 ? n : null;
}

export interface Dice {
  readonly seen: readonly number[];
  readonly rolls: readonly number[];
  /** 말 자리(LE05 판 · 0~15) */
  readonly pos: number;
  /** 굴린 횟수 · 말이 간 칸 합(별 둘 · 셋 — 판 바퀴 = moved ÷ 15) */
  readonly count?: number;
  readonly moved?: number;
  readonly prev: string | null;
  readonly wasShake: boolean;
  /** 흔든 뒤 지난 시간(가위바위보와 같다 — ARM_MS) */
  readonly armed: number | null;
  readonly since: number;
}

export const initialDice = (): Dice => ({ seen: [], rolls: [], pos: 0, prev: null, wasShake: false, armed: null, since: ROUND_GAP_MS });

export function stepDice(s: Dice, dtMs: number, frame: MbFrame | null): Dice {
  const since = s.since + dtMs;
  if (!frame) return { ...s, since };
  const bits = bitsOfLeds(frame.leds);
  const armed = tickArm(s.armed, rise(frame.shake, s.wasShake), dtMs);
  const face = faceOf(frame.leds);
  const fresh = face !== null && (bits !== s.prev || (armed !== null && armed >= ARM_MS)) && since >= ROUND_GAP_MS;
  if (!fresh) return { ...s, prev: bits, wasShake: frame.shake, armed, since };
  return {
    seen: s.seen.includes(face) ? s.seen : [...s.seen, face].sort(),
    rolls: [...s.rolls, face].slice(-12),
    pos: Math.min(BOARD_LEN, s.pos + face),
    count: (s.count ?? 0) + 1,
    moved: (s.moved ?? 0) + face,
    prev: bits, wasShake: frame.shake, armed: null, since: 0,
  };
}
export const allFaces = (s: Dice): boolean => s.seen.length >= 6;

// ── 볼 미로(L12) ─────────────────────────────────────────────────────

/** 7×7 미로 — `#` 벽 · `S` 출발 · `H` 구멍 */
export const MAZE: readonly string[] = [
  "S..#...",
  ".#.#.#.",
  ".#...#.",
  ".####..",
  "......#",
  "#.###.#",
  "....#.H",
];
export const MAZE_N = MAZE.length;
/** 기울기 문턱(mg) · 1g(1000mg)에서 초당 칸 수 */
export const BALL_DEAD_MG = 150;
export const BALL_SPEED = 3;

export interface Ball { readonly x: number; readonly y: number; readonly done: boolean }

const findCell = (ch: string) => { for (let y = 0; y < MAZE_N; y++) { const x = MAZE[y]!.indexOf(ch); if (x >= 0) return { x, y }; } return { x: 0, y: 0 }; };
const HOLE = findCell("H");
export const initialBall = (): Ball => ({ ...findCell("S"), done: false });

const wall = (x: number, y: number) => {
  const cx = Math.round(x), cy = Math.round(y);
  return cx < 0 || cy < 0 || cx >= MAZE_N || cy >= MAZE_N || MAZE[cy]![cx] === "#";
};
const clampCell = (v: number) => Math.max(0, Math.min(MAZE_N - 1, v));
const axis = (mg: number) => (Math.abs(mg) < BALL_DEAD_MG ? 0 : Math.max(-1, Math.min(1, mg / 1000)));

/** 앞으로 기울이면(Y 음수) 위로 · 오른쪽(X 양수)이면 오른쪽 — 리모컨 방향(lib/live toRemote)과 같다. 벽은 축마다 따로 막는다 */
export function stepBall(s: Ball, dtMs: number, frame: MbFrame | null): Ball {
  if (s.done || !frame) return s;
  const k = (BALL_SPEED * dtMs) / 1000;
  let { x, y } = s;
  const nx = clampCell(x + axis(frame.ax) * k);
  if (!wall(nx, y)) x = nx;
  const ny = clampCell(y + axis(frame.ay) * k);
  if (!wall(x, ny)) y = ny;
  return { x, y, done: Math.round(x) === HOLE.x && Math.round(y) === HOLE.y };
}

// ── 수평계(L14) ──────────────────────────────────────────────────────

export const LEVEL_MG = 250;
export const LEVEL_HOLD_MS = 3000;
export const BRICKS_GOAL = 3;
export const BRICKS_MAX = 30;

export interface Level {
  readonly held: number;
  readonly bricks: number;
  /** 벽돌을 올린 뒤 체크가 한 번 내려가야 다음 벽돌 */
  readonly needRelease: boolean;
}

export const initialLevel = (): Level => ({ held: 0, bricks: 0, needRelease: false });
/** 수평 + 체크 — 보드가 수평이 아닌데 체크를 그리면 아이의 프로그램이 다르게 재는 것이다 */
export const isLevelCheck = (f: MbFrame | null): boolean => !!f && iconOf(f.leds) === "Yes" && Math.abs(f.ax) < LEVEL_MG && Math.abs(f.ay) < LEVEL_MG;

export function stepLevel(s: Level, dtMs: number, frame: MbFrame | null): Level {
  if (s.bricks >= BRICKS_MAX) return s;
  if (!isLevelCheck(frame)) return s.held === 0 && !s.needRelease ? s : { ...s, held: 0, needRelease: false };
  if (s.needRelease) return s;
  const held = s.held + dtMs;
  return held >= LEVEL_HOLD_MS ? { held: 0, bricks: s.bricks + 1, needRelease: true } : { ...s, held };
}
